import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, MEDIA_BASE_URL } from '../../api';
import { ChevronLeftIcon, ChevronRightIcon, X as CloseIconLucide, Download, Play, Pause, Volume2, VolumeX, SkipBack, SkipForward, Maximize, Settings } from 'lucide-react';
import ReactPlayer from 'react-player/lazy';

interface ClipData {
  id: string;
  clipId?: string | null;
  videoSrc?: string | null;
  jobId?: string | null;
  llmOutputId?: string | null;
  clipType?: 'original' | 'edited';
  duration?: number | null;
  createdAt?: string;
  vodTitle?: string | null;
}

interface ClipDetailPopupProps {
  title?: string | null;
  clipId?: string | null;
  videoSrc?: string | null;
  jobId?: string | null;
  llmOutputId?: string | null;
  isOpen: boolean;
  onClose: () => void;
  clipType?: 'original' | 'edited';
  allClips: ClipData[];
  currentClipIndex: number;
  onNavigate: (newIndex: number) => void;
  duration?: number | null;
  createdAt?: string;
  vodTitle?: string | null;
}

const ClipDetailPopup: React.FC<ClipDetailPopupProps> = ({
  title,
  clipId,
  videoSrc,
  jobId,
  llmOutputId,
  isOpen,
  onClose,
  clipType,
  allClips,
  currentClipIndex,
  onNavigate,
  duration,
  createdAt,
  vodTitle,
}) => {
  const [llmReasoning, setLlmReasoning] = useState<string | null>(null);
  const [isLoadingLlm, setIsLoadingLlm] = useState<boolean>(false);
  const [errorLlm, setErrorLlm] = useState<string | null>(null);
  const navigate = useNavigate();
  
  // Enhanced player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isBuffering, setIsBuffering] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const playerRef = useRef<ReactPlayer>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({});
  const [videoPlayerStyle, setVideoPlayerStyle] = useState<React.CSSProperties>({});
  const [llmPanelStyle, setLlmPanelStyle] = useState<React.CSSProperties>({});

  // Enhanced player control functions
  const togglePlay = useCallback(() => {
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const toggleMute = useCallback(() => {
    setIsMuted(!isMuted);
  }, [isMuted]);

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  }, []);

  const handlePlaybackRateChange = useCallback((rate: number) => {
    setPlaybackRate(rate);
    setShowSettings(false);
  }, []);

  const skipBackward = useCallback(() => {
    if (playerRef.current) {
      const newTime = Math.max(0, currentTime - 10);
      playerRef.current.seekTo(newTime, 'seconds');
      setCurrentTime(newTime);
    }
  }, [currentTime]);

  const skipForward = useCallback(() => {
    if (playerRef.current) {
      const newTime = Math.min(totalDuration, currentTime + 10);
      playerRef.current.seekTo(newTime, 'seconds');
      setCurrentTime(newTime);
    }
  }, [currentTime, totalDuration]);

  const handleProgress = useCallback((state: { played: number; playedSeconds: number }) => {
    setCurrentTime(state.playedSeconds);
  }, []);

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (playerRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = clickX / rect.width;
      const newTime = percentage * totalDuration;
      playerRef.current.seekTo(newTime, 'seconds');
      setCurrentTime(newTime);
    }
  }, [totalDuration]);

  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  }, [isPlaying]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement && containerRef.current) {
      containerRef.current.requestFullscreen();
      setIsFullscreen(true);
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  const handleVideoEnd = useCallback(() => {
    setIsPlaying(false);
    // Auto-advance to next clip if available
    if (canGoNext && currentClipIndex < allClips.length - 1) {
      setTimeout(() => {
        onNavigate(currentClipIndex + 1);
      }, 1000);
    }
  }, [currentClipIndex, allClips.length, onNavigate]);

  const formatTime = useCallback((seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  // Component cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup video resources when component unmounts
      if (playerRef.current) {
        const internalPlayer = playerRef.current.getInternalPlayer();
        if (internalPlayer && internalPlayer instanceof HTMLVideoElement) {
          internalPlayer.pause();
          internalPlayer.removeAttribute('src');
          internalPlayer.load();
        }
      }
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case ' ':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          skipBackward();
          break;
        case 'ArrowRight':
          e.preventDefault();
          skipForward();
          break;
        case 'ArrowUp':
          e.preventDefault();
          setVolume(prev => Math.min(1, prev + 0.1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setVolume(prev => Math.max(0, prev - 0.1));
          break;
        case 'm':
        case 'M':
          e.preventDefault();
          toggleMute();
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          toggleFullscreen();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, togglePlay, skipBackward, skipForward, toggleMute, toggleFullscreen]);

  // Reset player state when clip changes
  useEffect(() => {
    if (isOpen) {
      setIsPlaying(true);
      setCurrentTime(0);
      setShowControls(true);
    }
  }, [clipId, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const screenPadding = window.innerWidth < 768 ? 40 : 120;
    const maxPopupWidth = window.innerWidth - screenPadding;
    const maxPopupHeight = window.innerHeight - screenPadding;

    const targetAspectRatio = clipType === 'edited' ? 9 / 16 : 16 / 9;

    let popupWidth: number, popupHeight: number;
    
    if (clipType === 'edited') {
      // For 9:16 clips, start with a wider popup to accommodate both video and panel
      const minPanelWidth = 300;
      const idealVideoHeight = maxPopupHeight * 0.9; // Use most of the screen height
      const idealVideoWidth = idealVideoHeight * targetAspectRatio;
      
      popupWidth = Math.min(idealVideoWidth + minPanelWidth + 50, maxPopupWidth); // +50 for padding
      popupHeight = Math.min(idealVideoHeight, maxPopupHeight);
    } else {
      // Original logic for 16:9 clips
      popupWidth = maxPopupWidth;
      popupHeight = popupWidth / targetAspectRatio;

      if (popupHeight > maxPopupHeight) {
        popupHeight = maxPopupHeight;
        popupWidth = popupHeight * targetAspectRatio;
      }
      
      popupWidth = Math.min(popupWidth, maxPopupWidth);
    }

    setPopupStyle({
      width: `${popupWidth}px`,
      height: `${popupHeight}px`,
    });

    if (clipType === 'edited') {
      const videoWidth = popupHeight * targetAspectRatio;
      const minPanelWidth = 300; // Minimum panel width
      const availableWidth = popupWidth - videoWidth;
      
      // Ensure we have enough space for the panel
      if (availableWidth < minPanelWidth) {
        const adjustedVideoWidth = popupWidth - minPanelWidth;
        setVideoPlayerStyle({ width: `${adjustedVideoWidth}px`, height: '100%', flexShrink: 0 });
      } else {
        setVideoPlayerStyle({ width: `${videoWidth}px`, height: '100%', flexShrink: 0 });
      }
      setLlmPanelStyle({ flexGrow: 1, height: '100%', overflowY: 'auto', minWidth: `${minPanelWidth}px` });
    } else {
      setVideoPlayerStyle({ flexBasis: '70%', height: '100%', flexShrink: 0 });
      setLlmPanelStyle({ flexBasis: '30%', height: '100%', overflowY: 'auto' });
    }

  }, [isOpen, clipType]);

  useEffect(() => {
    // Use clipId for the API call, as the backend route expects it.
    if (isOpen && jobId && clipId) {
      setIsLoadingLlm(true);
      setErrorLlm(null);
      setLlmReasoning(null);
      const fetchLlmOutput = async () => {
        try {
          // The llmOutputId is used by the backend to find the specific reasoning text,
          // but the route itself needs the clipId to locate the parent clip and job.
          const response = await api.get<{ reasoningText: string }>(`/clips/llm-output/${jobId}/${clipId}`);
          setLlmReasoning(response.data.reasoningText);
        } catch (err: any) {
          const errorMessage = err.response?.data?.message || err.message || 'Failed to load LLM reasoning.';
          setErrorLlm(errorMessage);
        } finally {
          setIsLoadingLlm(false);
        }
      };
      // Only fetch if there's an llmOutputId to look for.
      if (llmOutputId) {
        fetchLlmOutput();
      } else {
        setIsLoadingLlm(false);
        setLlmReasoning('Analysis is not available for this clip.');
      }
    }
  }, [isOpen, jobId, clipId, llmOutputId]);

  const handleEdit = () => {
    if (jobId && videoSrc) {
      const fileName = videoSrc.split('/').pop();
      if (!fileName) {
        alert('Could not determine the file name from the video source.');
        return;
      }
      // Use the correct streaming URL for the editor
      const fullVideoUrlForEditor = clipType === 'edited' 
        ? `${MEDIA_BASE_URL}/api/jobs/clips/edited/${jobId}/${fileName}`
        : `${MEDIA_BASE_URL}/api/jobs/clips/file/${jobId}/${fileName}`;
      const queryParams = new URLSearchParams({
        jobId: jobId,
        clipId: clipId || '',
        videoUrl: fullVideoUrlForEditor,
        returnUrl: window.location.href,
      }).toString();
      // Get editor URL from runtime config first, then fall back to build-time env
      const editorBaseUrl = (window as any).__CLIPOID_CONFIG__?.EDITOR_URL || 
                           import.meta.env['VITE_EDITOR_URL'] || 
                           'https://editor.clipoid.com';
      const fullEditorUrl = `${editorBaseUrl}?${queryParams}`;
      window.open(fullEditorUrl, '_blank');
      onClose();
    } else {
      alert('Clip information is missing, cannot open editor.');
    }
  };

  // Reset player state when popup opens/closes or clip changes
  useEffect(() => {
    if (isOpen) {
      // Reset all states when popup opens or clip changes
      setIsPlaying(false);
      setCurrentTime(0);
      setShowControls(true);
      setIsFullscreen(false);
      
      // Clear any existing timeouts
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    } else {
      // Clean up when popup closes
      setIsPlaying(false);
      setCurrentTime(0);
      if (playerRef.current) {
        playerRef.current.seekTo(0, 'seconds');
        // Force cleanup of video element
        const internalPlayer = playerRef.current.getInternalPlayer();
        if (internalPlayer && internalPlayer instanceof HTMLVideoElement) {
          internalPlayer.pause();
          internalPlayer.removeAttribute('src');
          internalPlayer.load();
        }
      }
    }
    
    // Cleanup function
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isOpen, clipId]); // Re-run when popup opens/closes or clip changes

  const handleDownload = async () => {
    const fileName = videoSrc ? videoSrc.split('/').pop() : null;
    const downloadVideoSrc = jobId && fileName 
      ? (clipType === 'edited' 
          ? `${MEDIA_BASE_URL}/api/jobs/clips/edited/${jobId}/${fileName}`
          : `${MEDIA_BASE_URL}/api/jobs/clips/file/${jobId}/${fileName}`)
      : null;
    
    if (!downloadVideoSrc) {
      alert('Video source is not available for download.');
      return;
    }
    
    console.log('[ClipDetailPopup] Download attempt:', {
      videoSrc,
      fileName,
      jobId,
      clipType,
      downloadVideoSrc,
      title,
      clipId
    });
    
    try {
      // Use API instance for authenticated requests
      const response = await api.get(downloadVideoSrc, {
        responseType: 'blob'
      });
      
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      // Sanitize the title to create a valid filename
      const safeTitle = title ? title.replace(/[^a-z0-9_.-]/gi, '_').toLowerCase() : 'clip';
      a.download = `${safeTitle}_${clipId}.mp4`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Download failed:', error);
      console.error('Download error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
        url: downloadVideoSrc
      });
      
      if (error.response?.status === 401) {
        alert('Authentication required. Please log in again.');
      } else if (error.response?.status === 404) {
        alert('Clip file not found. It may have been deleted or moved.');
      } else if (error.response?.status === 403) {
        alert('Access denied. You do not have permission to download this clip.');
      } else {
        alert(`Download failed: ${error.message || 'Unknown error'}. Please try again.`);
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  const fileName = videoSrc ? videoSrc.split('/').pop() : null;
  const fullVideoSrc = jobId && fileName 
    ? (clipType === 'edited' 
        ? `${MEDIA_BASE_URL}/api/jobs/clips/edited/${jobId}/${fileName}`
        : `${MEDIA_BASE_URL}/api/jobs/clips/file/${jobId}/${fileName}`)
    : null;
  
  const canGoPrevious = currentClipIndex > 0;
  const canGoNext = currentClipIndex < allClips.length - 1;

  const handlePrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (canGoPrevious) {
      onNavigate(currentClipIndex - 1);
    }
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (canGoNext) {
      onNavigate(currentClipIndex + 1);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" onClick={onClose}>
      {isOpen && canGoPrevious && (
        <button onClick={handlePrevious} className="fixed left-2 sm:left-4 md:left-10 top-1/2 -translate-y-1/2 z-[51] p-2 sm:p-3 bg-gray-800/50 hover:bg-gray-700/70 rounded-full text-white shadow-lg transition-colors" aria-label="Previous clip">
          <ChevronLeftIcon className="h-6 w-6 sm:h-8 sm:w-8" />
        </button>
      )}

      <div className="bg-card rounded-lg shadow-xl flex flex-col overflow-hidden w-full h-full max-w-[95vw] max-h-[95vh] md:max-w-none md:max-h-none border border-border" style={popupStyle} onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center p-3 sm:p-4 border-b border-border flex-shrink-0">
          <h2 className="text-lg sm:text-xl font-semibold text-foreground truncate" title={title || 'Clip Details'}>{title || 'Clip Details'}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Close popup">
            <CloseIconLucide size={24} />
          </button>
        </div>

        <div className="flex flex-col md:flex-row flex-grow overflow-hidden">
          <div className="bg-black relative flex items-center justify-center" style={videoPlayerStyle}>
            {fullVideoSrc ? (
               <div 
                ref={containerRef}
                className="relative w-full h-full group"
                onMouseMove={handleMouseMove}
                onMouseLeave={() => setShowControls(false)}
              >
                <ReactPlayer
                  ref={playerRef}
                  key={clipId}
                  url={fullVideoSrc}
                  playing={isPlaying}
                  muted={isMuted}
                  volume={volume}
                  playbackRate={playbackRate}
                  width="100%"
                  height="100%"
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onProgress={handleProgress}
                  onDuration={setTotalDuration}
                  onBuffer={() => setIsBuffering(true)}
                  onBufferEnd={() => setIsBuffering(false)}
                  onEnded={handleVideoEnd}
                  config={{
                    file: {
                      attributes: {
                        style: {
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        },
                        preload: 'metadata',
                      },
                    },
                  }}
                />
                
                {/* Custom Controls Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30 transition-opacity duration-300 ${showControls || isBuffering ? 'opacity-100' : 'opacity-0'}`}>
                  {/* Buffering Indicator */}
                  {isBuffering && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                    </div>
                  )}
                  
                  {/* Play/Pause Center Button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button
                      onClick={togglePlay}
                      className={`p-4 rounded-full bg-black/50 text-white hover:bg-black/70 transition-all duration-200 ${showControls ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}
                    >
                      {isPlaying ? <Pause size={32} /> : <Play size={32} />}
                    </button>
                  </div>
                  
                  {/* Bottom Controls */}
                  <div className={`absolute bottom-0 left-0 right-0 p-4 transition-all duration-300 ${showControls ? 'translate-y-0' : 'translate-y-full'}`}>
                    {/* Progress Bar */}
                    <div className="mb-3">
                      <div 
                        className="relative w-full h-1 bg-white/30 rounded cursor-pointer hover:h-2 transition-all duration-200"
                        onClick={handleSeek}
                      >
                        <div 
                          className="absolute top-0 left-0 h-full bg-blue-500 rounded"
                          style={{ width: `${(currentTime / totalDuration) * 100}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-xs text-white/80 mt-1">
                        <span>{formatTime(currentTime)}</span>
                        <span>{formatTime(totalDuration)}</span>
                      </div>
                    </div>
                    
                    {/* Control Buttons */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <button onClick={togglePlay} className="p-2 text-white hover:text-blue-400 transition-colors">
                          {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                        </button>
                        <button onClick={skipBackward} className="p-2 text-white hover:text-blue-400 transition-colors">
                          <SkipBack size={18} />
                        </button>
                        <button onClick={skipForward} className="p-2 text-white hover:text-blue-400 transition-colors">
                          <SkipForward size={18} />
                        </button>
                        
                        {/* Volume Control */}
                        <div className="flex items-center space-x-2 group/volume">
                          <button onClick={toggleMute} className="p-2 text-white hover:text-blue-400 transition-colors">
                            {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                          </button>
                          <div className="opacity-70 group-hover/volume:opacity-100 transition-opacity duration-200">
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.1"
                              value={isMuted ? 0 : volume}
                              onChange={handleVolumeChange}
                              className="w-16 h-2 bg-white/30 rounded-lg appearance-none cursor-pointer slider"
                              style={{
                                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(isMuted ? 0 : volume * 100)}%, rgba(255,255,255,0.3) ${(isMuted ? 0 : volume * 100)}%, rgba(255,255,255,0.3) 100%)`
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {/* Playback Speed */}
                        <div className="relative">
                          <button 
                            onClick={() => setShowSettings(!showSettings)}
                            className="p-2 text-white hover:text-blue-400 transition-colors"
                          >
                            <Settings size={18} />
                          </button>
                          {showSettings && (
                            <div className="absolute bottom-full right-0 mb-2 bg-black/90 rounded-lg p-2 min-w-[120px]">
                              <div className="text-white text-xs mb-2">Playback Speed</div>
                              {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 2].map(rate => (
                                <button
                                  key={rate}
                                  onClick={() => handlePlaybackRateChange(rate)}
                                  className={`block w-full text-left px-2 py-1 text-xs rounded ${
                                    playbackRate === rate ? 'bg-blue-500 text-white' : 'text-white/80 hover:bg-white/10'
                                  }`}
                                >
                                  {rate}x {rate === 1 ? '(Normal)' : ''}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <button onClick={toggleFullscreen} className="p-2 text-white hover:text-blue-400 transition-colors">
                          <Maximize size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center text-muted-foreground">
                Video not available.
              </div>
            )}
          </div>

          <div className="p-4 sm:p-6 flex flex-col space-y-4 bg-secondary w-full md:w-auto overflow-y-auto" style={llmPanelStyle}>
            {/* Clip Details Section */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Clip Details</h3>
              <div className="space-y-1.5 text-sm text-muted-foreground">
                {duration && (
                  <p><strong className="font-medium text-foreground">Duration:</strong> {Math.round(duration)}s</p>
                )}
                {createdAt && (
                  <p><strong className="font-medium text-foreground">Created:</strong> {new Date(createdAt).toLocaleDateString()}</p>
                )}
                {vodTitle && (
                   <p><strong className="font-medium text-foreground">Source VOD:</strong> {vodTitle}</p>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-border"></div>

            {/* Highlight Analysis Section */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Highlight Analysis</h3>
              {!llmOutputId ? (
                <p className="text-muted-foreground text-sm">Analysis is not available for this clip.</p>
              ) : isLoadingLlm ? (
                <p className="text-muted-foreground text-sm">Loading analysis...</p>
              ) : errorLlm ? (
                <p className="text-error-dark text-sm">{errorLlm}</p>
              ) : llmReasoning ? (
                <p className="text-muted-foreground text-sm whitespace-pre-wrap">{llmReasoning}</p>
              ) : (
                <p className="text-muted-foreground text-sm">No analysis found for this clip.</p>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="mt-auto pt-4 space-y-2">
              <button
                onClick={handleDownload}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors text-sm flex items-center justify-center"
              >
                <Download size={16} className="mr-2" />
                Download Clip
              </button>
              {(!clipType || clipType === 'original') && (
                <button onClick={handleEdit} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-2 px-4 rounded-lg transition-colors text-sm">
                  Edit Clip
                </button>
              )}
              {clipType === 'edited' && (
                <div className="text-center text-xs text-muted-foreground pt-2">
                  This is an edited version of the clip. Re-editing is not currently supported.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {isOpen && canGoNext && (
        <button onClick={handleNext} className="fixed right-2 sm:right-4 md:right-10 top-1/2 -translate-y-1/2 z-[51] p-2 sm:p-3 bg-gray-800/50 hover:bg-gray-700/70 rounded-full text-white shadow-lg transition-colors" aria-label="Next clip">
          <ChevronRightIcon className="h-6 w-6 sm:h-8 sm:w-8" />
        </button>
      )}
    </div>
  );
};

export default ClipDetailPopup;